import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { EmissionDesignatorOperationModalData } from './models/emission-designator-operation-modal-data';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DropdownService } from '../../../../../../core/services/resources/dropdown.service';
import { ResourceDataKeys } from '../../../../../../core/constants/resource-data-keys';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { MatChipsModule } from '@angular/material/chips';
import { Observable, startWith, Subject, takeUntil, tap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import {
    FormArray,
    FormControl,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { EmissionDesignator } from '../../models/emission-designator';
import { EmissionDesignatorFormService } from '../../services/emission-designator-form/emission-designator-form.service';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../../../../shared/directives/regex-input/regex-input.directive';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { EmissionDesignatorOperationFormService } from '../../services/emission-designator-operation-form/emission-designator-operation-form.service';
import { BaseEmissionDesignatorOperationComponent } from '../base-emission-designator-operation/base-emission-designator-operation.component';
import { EmissionDesignatorOperation } from '../../models/emission-designator-operation';
import { EmissionDesignatorConstants } from '../../constants/emission-designator-constants';
import { DraggableModalWrapperComponent } from '../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';

@Component({
    selector: 'app-emission-designator-operation',
    imports: [
        MatButtonModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        RegexInputDirective,
        DraggableModalWrapperComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './emission-designator-operation.component.html',
})
export class EmissionDesignatorOperationComponent
    extends BaseEmissionDesignatorOperationComponent<EmissionDesignatorOperation>
    implements OnInit, OnDestroy
{
    patterns = RegExPattern;
    emissionDesignatorConstants = EmissionDesignatorConstants;

    destroy$: Subject<void> = new Subject<void>();

    allApprovedFccEmissionDesignators: DropdownOption[] = [];

    commonEmissionDesignatorsFromAllFrequencies: EmissionDesignator[] = [];

    availableToAddEmissionDesignatorOptions: DropdownOption[] = [];

    autoCompleteFilteredAddEmissionDesignatorOptions: DropdownOption[] = [];
    autoCompleteFilteredDeleteEmissionDesignatorOptions: DropdownOption[] = [];

    isManualEmissionOption: boolean = false;

    form = this.emissionDesignatorOperationFormService.getFormDefinition();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public override data: EmissionDesignatorOperationModalData,
        protected override readonly dialogRef: MatDialogRef<EmissionDesignatorOperationComponent>,
        private readonly dropdownService: DropdownService,
        private readonly emissionDesignatorFormService: EmissionDesignatorFormService,
        private readonly emissionDesignatorOperationFormService: EmissionDesignatorOperationFormService,
    ) {
        super(data, dialogRef);
    }

    get addEmissionDesignatorFormControl(): FormControl {
        return this.form.controls.addEmissionDesignator as FormControl;
    }

    get deleteEmissionDesignatorFormControl(): FormControl {
        return this.form.controls.deleteEmissionDesignator as FormControl;
    }

    ngOnInit(): void {
        this.getApprovedFccEmissionDesignators().subscribe((data) => {
            this.allApprovedFccEmissionDesignators = data;
            this.availableToAddEmissionDesignatorOptions = data;

            this.commonEmissionDesignatorsFromAllFrequencies =
                this.emissionDesignatorFormService.getCommonEmissionDesignatorsFromAllFrequencies(
                    this.data.controls,
                );

            if (this.singleFrequencyAction === true) {
                this.initializeFormForSingleFrequency();
            } else {
                this.initializeFormForMultipleFrequencies();
            }

            this.addEmissionDesignatorFormControl.valueChanges
                .pipe(
                    startWith(this.addEmissionDesignatorFormControl.value),
                    takeUntil(this.destroy$),
                    tap((value) => {
                        this.filterAddEmissionDesignatorAutoCompleteOptions(
                            value,
                        );

                        this.isManualEmissionOption =
                            value.length > 0 &&
                            this.isManualEmissionDesignatorOption(value);
                    }),
                )
                .subscribe();

            this.deleteEmissionDesignatorFormControl.valueChanges
                .pipe(
                    startWith(this.deleteEmissionDesignatorFormControl.value),
                    takeUntil(this.destroy$),
                    tap((value) => {
                        this.filterDeleteEmissionDesignatorAutoCompleteOptions(
                            value,
                        );
                    }),
                )
                .subscribe();
        });
    }

    onAddEmissionDesignator(event: MatAutocompleteSelectedEvent): void {
        const code = event.option.value as string;
        const emissionDesignator: EmissionDesignator = {
            id: null,
            code,
            actionCode: ActionCode.Add,
            manualEmissionCode: this.isManualEmissionDesignatorOption(code),
        };

        this.emissionDesignatorFormService.addEmissionDesignator(
            emissionDesignator,
            this.addedEmissionDesignatorsFormArray,
        );

        this.refreshAddEmissionDesignatorDropdownOptions();

        this.addEmissionDesignatorFormControl.setValue('');
    }

    onDeleteEmissionDesignator(event: MatAutocompleteSelectedEvent): void {
        const deleteEmissionDesignator =
            this.emissionDesignatorFormService.getFormDefinition();
        deleteEmissionDesignator.controls.code.setValue(event.option.value);

        this.deletedEmissionDesignatorsFormArray.push(deleteEmissionDesignator);
        this.deleteEmissionDesignatorFormControl.setValue('');

        this.refreshDeleteEmissionDesignatorDropdownOptions();
    }

    onRemoveAddedEmissionDesignator(code: string): void {
        const control = this.addedEmissionDesignatorsFormArray;
        const index = control.controls.findIndex(
            (control) => control.value.code === code,
        );

        control.removeAt(index);

        this.refreshAddEmissionDesignatorDropdownOptions();

        this.form.markAsTouched();
    }

    onRemoveDeletedEmissionDesignator(code: string): void {
        const control = this.deletedEmissionDesignatorsFormArray;
        const index = control.controls.findIndex(
            (control) => control.value.code === code,
        );

        control.removeAt(index);

        this.refreshDeleteEmissionDesignatorDropdownOptions();
    }

    onSave(): void {
        if (this.form.touched === true) {
            this.data.saveEmissionDesignatorOperationFunction(
                this.form,
                this.data.controls,
            );
        }

        this.dialogRef.close();
    }

    shouldShowManualEmissionDesignatorOption(): boolean {
        return this.emissionDesignatorFormService.shouldShowManualEmissionDesignatorOption(
            this.isManualEmissionOption,
            this.addEmissionDesignatorFormControl,
        );
    }

    private get addedEmissionDesignatorsFormArray(): FormArray {
        return this.form.controls.addedEmissionDesignators as FormArray;
    }

    private get deletedEmissionDesignatorsFormArray(): FormArray {
        return this.form.controls.deletedEmissionDesignators as FormArray;
    }

    protected isManualEmissionDesignatorOption(code: string): boolean {
        return !this.allApprovedFccEmissionDesignators.some(
            (option) => option.value === code.toUpperCase(),
        );
    }

    private getApprovedFccEmissionDesignators(): Observable<DropdownOption[]> {
        return this.dropdownService.get(ResourceDataKeys.emissionDesignator, {
            allowEmpty: false,
            excludeDescriptions: true,
        });
    }

    private initializeFormForSingleFrequency(): void {
        this.commonEmissionDesignatorsFromAllFrequencies.forEach(
            (emissionDesignator) => {
                this.emissionDesignatorFormService.addEmissionDesignator(
                    emissionDesignator,
                    this.addedEmissionDesignatorsFormArray,
                );
            },
        );

        this.refreshAddEmissionDesignatorDropdownOptions();
        this.refreshDeleteEmissionDesignatorDropdownOptions();
    }

    private initializeFormForMultipleFrequencies(): void {
        let list = this.allApprovedFccEmissionDesignators;

        this.commonEmissionDesignatorsFromAllFrequencies.forEach(
            (emissionDesignator) => {
                list =
                    this.emissionDesignatorFormService.getEmissionDesignatorsListWithoutCode(
                        list,
                        emissionDesignator.code!,
                    );
            },
        );

        this.availableToAddEmissionDesignatorOptions = list;
    }

    private filterAddEmissionDesignatorAutoCompleteOptions(
        value: string,
    ): void {
        this.autoCompleteFilteredAddEmissionDesignatorOptions =
            this.emissionDesignatorFormService.getFilteredAddEmissionDesignatorAutoCompleteOptions(
                value,
                this.availableToAddEmissionDesignatorOptions,
            );
    }

    private filterDeleteEmissionDesignatorAutoCompleteOptions(
        value: string,
    ): void {
        this.autoCompleteFilteredDeleteEmissionDesignatorOptions =
            this.emissionDesignatorOperationFormService.getFilteredDeleteEmissionDesignatorAutoCompleteOptions(
                value,
                this.data.controls,
                this.deletedEmissionDesignatorsFormArray,
            );
    }

    private refreshAddEmissionDesignatorDropdownOptions(): void {
        const list =
            this.emissionDesignatorFormService.getAddEmissionDesignatorDropdownOptions(
                this.data.controls,
                this.allApprovedFccEmissionDesignators,
                this.addedEmissionDesignatorsFormArray,
                this.singleFrequencyAction,
            );

        this.availableToAddEmissionDesignatorOptions = list;
        this.autoCompleteFilteredAddEmissionDesignatorOptions = list;
    }

    private refreshDeleteEmissionDesignatorDropdownOptions(): void {
        const list =
            this.emissionDesignatorOperationFormService.getDeleteEmissionDesignatorDropdownOptions(
                this.data.controls,
                this.deletedEmissionDesignatorsFormArray,
            );

        this.autoCompleteFilteredDeleteEmissionDesignatorOptions = list;
    }
}
